import { Button } from '@neo/primitives/button'
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '@neo/primitives/dropdown-menu'
import { Tooltip } from '@neo/primitives/tooltip'
import { downloadFile } from '@shared/utils'
import { useMutation } from '@tanstack/react-query'
import type { AxiosError } from 'axios'
import { MoreVertical } from 'lucide-react'
import { useState } from 'react'
import { useParams } from 'router-v6'
import { queryClient } from 'v2/configs/rq-setup'
import { useFlowsQuery } from 'v2/pages/settings/queries'
import { flowService } from 'v2/shared/services/flow-service'
import { getErrorText } from 'v2/shared/utils/api-error-text'
import { flowQueryKeys } from 'v2/shared/utils/query-key-management'
import { errorToast, successToast } from 'v2/utils/toasts'
import type { KBRow } from './detail.config'
import { RenameKBDialog } from './rename-modal'
import { KBDocumentSource, type RKnowledgeBase } from './types'
import { useAppNavigate } from 'v2/shared/hooks/use-app-navigate'

// The fetchable file URL for the Download action. v2/rag supplies `download_url` (the S3 ref); v1/aiassist
// file docs carry no `download_url` — their fetchable URL rides in `name` (`display_name` is the human
// filename). Accept whichever is a real http(s) URL so v1 file docs download too, while a bare filename is
// rejected (downloading that fetched the SPA's index.html — the old bug). No real URL → null → item hidden.
const resolveDownloadUrl = (row?: KBRow | null): string | null => {
  for (const candidate of [row?.download_url, row?.name]) {
    if (typeof candidate === 'string' && /^https?:\/\//i.test(candidate)) return candidate
  }
  return null
}

interface KBActionsProps {
  row?: KBRow
  isHeader?: boolean
}

export const KBActions = ({ row, isHeader = false }: KBActionsProps) => {
  const [open, setOpen] = useState(false)
  const navigate = useAppNavigate()
  const { id } = useParams()
  const { data: flows } = useFlowsQuery((s) => s)

  const kbDetails = queryClient.getQueryData<RKnowledgeBase>(
    flowQueryKeys().knowledgeBase(id || ''),
  )

  const [dialogConfig, setDialogConfig] = useState<{
    open: boolean
    kb: RKnowledgeBase | null
  }>({
    open: false,
    kb: null,
  })

  const { isSource, source, enabled } = row || {}

  const deleteMutation = useMutation({
    mutationFn: async () => {
      if (isHeader) {
        if (!kbDetails?.id) return
        await flowService.deleteKnowledgeBase(kbDetails.id)
      } else if (isSource) {
        await flowService.deleteKnowledgeBaseSource({
          sourceId: row?.id || '',
          kbId: row?.parentKnowledgeBaseId || '',
        })
      } else {
        await flowService.deleteDocument({
          documentId: row?.id || '',
          kbId: row?.parentKnowledgeBaseId || '',
        })
      }
    },
    onSuccess: () => {
      if (isHeader) {
        navigate('/agent/knowledge-base')
        queryClient.invalidateQueries({ queryKey: flowQueryKeys().knowledgeBases })
        if (id) queryClient.invalidateQueries({ queryKey: flowQueryKeys().knowledgeBase(id) })
        successToast({
          title: 'Deleted',
          description: 'Knowledge base deleted successfully.',
        })
      } else {
        queryClient.invalidateQueries({
          queryKey: flowQueryKeys().knowledgeBase(row?.parentKnowledgeBaseId || ''),
        })
        // Also refresh the KB LIST (agent tab + standalone): its per-KB rollup status changes when a
        // source/document is deleted (e.g. removing the only failed source flips "Ready (partial)" →
        // "Ready"). Without this the list keeps showing the stale pre-delete status until a reload.
        queryClient.invalidateQueries({ queryKey: flowQueryKeys().knowledgeBases })
        successToast({
          title: 'Deleted',
          description: isSource ? 'Source deleted successfully.' : 'Document deleted successfully.',
        })
      }
    },
    onError: (error) => {
      errorToast({ title: 'Error', description: getErrorText(error as AxiosError) })
    },
  })

  const resyncMutation = useMutation({
    mutationFn: async () => {
      if (isHeader) {
        if (!kbDetails?.id) return
        await flowService.syncKnowledgeBase(kbDetails.id)
      } else if (isSource) {
        await flowService.syncKnowledgeBaseSource({
          sourceId: row?.id || '',
          kbId: row?.parentKnowledgeBaseId || '',
        })
      } else {
        await flowService.syncKnowledgeBaseDocument({
          documentId: row?.id || '',
          kbId: row?.parentKnowledgeBaseId || '',
        })
      }
    },
    onSuccess: () => {
      if (isHeader) {
        queryClient.invalidateQueries({ queryKey: flowQueryKeys().knowledgeBases })
        if (id) queryClient.invalidateQueries({ queryKey: flowQueryKeys().knowledgeBase(id) })
        successToast({
          title: 'Resync started',
          description: 'Knowledge base resync started.',
        })
      } else {
        queryClient.invalidateQueries({
          queryKey: flowQueryKeys().knowledgeBase(row?.parentKnowledgeBaseId || ''),
        })
        // Refresh the list too so its rollup status flips back to Syncing (out of Failed) immediately.
        queryClient.invalidateQueries({ queryKey: flowQueryKeys().knowledgeBases })
        successToast({
          title: 'Resync started',
          description: isSource ? 'Source resync started.' : 'Document resync started.',
        })
      }
    },
    onError: (error) => {
      errorToast({ title: 'Error', description: getErrorText(error as AxiosError) })
    },
  })

  const toggleEnabledMutation = useMutation({
    mutationFn: async () => {
      if (isHeader) {
        if (!kbDetails?.id) return
        await flowService.updateKnowledgeBase({ enabled: !kbDetails.enabled }, kbDetails.id)
      } else if (!isSource) {
        await flowService.updateDocument(
          { enabled: !enabled },
          row?.id || '',
          row?.parentKnowledgeBaseId || '',
        )
      }
    },
    onSuccess: () => {
      if (isHeader) {
        queryClient.invalidateQueries({ queryKey: flowQueryKeys().knowledgeBases })
        successToast({
          title: kbDetails?.enabled ? 'Disabled' : 'Enabled',
          description: `Knowledge base ${kbDetails?.enabled ? 'disabled' : 'enabled'} successfully.`,
        })
      } else {
        queryClient.invalidateQueries({
          queryKey: flowQueryKeys().knowledgeBase(row?.parentKnowledgeBaseId || ''),
        })
        // Refresh the list too: disabling/enabling a document can change the KB's effective readiness.
        queryClient.invalidateQueries({ queryKey: flowQueryKeys().knowledgeBases })
        successToast({
          title: enabled ? 'Disabled' : 'Enabled',
          description: `Document ${enabled ? 'disabled' : 'enabled'} successfully.`,
        })
      }
    },
    onError: (error) => {
      errorToast({ title: 'Error', description: getErrorText(error as AxiosError) })
    },
  })

  const handleDownload = async () => {
    if (!row) return
    // download_url is the fetchable file URL (rag `ref` → the uploader/S3 object). The old code used
    // `document_name` (a bare filename), so downloadFile() fetched it against the console origin and
    // got back the SPA's index.html — "some random file". Only file docs that carry a real URL can
    // be downloaded; for anything else the menu item isn't shown (see render guard below).
    const documentUrl = resolveDownloadUrl(row)
    const fileName = row.document_name || row.display_name || row.name
    if (!documentUrl) {
      errorToast({
        title: 'Download unavailable',
        description: 'No downloadable file for this item.',
      })
      return
    }
    try {
      await downloadFile(documentUrl, fileName)
      successToast({
        title: 'Download started',
        description: 'File download has started.',
      })
    } catch (error) {
      errorToast({ title: 'Download failed', description: getErrorText(error as AxiosError) })
    }
  }

  if (isHeader && !kbDetails) return null

  return (
    <>
      <DropdownMenu open={open} onOpenChange={setOpen}>
        <DropdownMenuTrigger asChild>
          <Button className="hover:bg-transparent p-2" variant={isHeader ? 'outline' : 'ghost'}>
            <Tooltip content="Actions">
              <MoreVertical size={16} />
            </Tooltip>
          </Button>
        </DropdownMenuTrigger>
        <DropdownMenuContent align="end" className="w-32">
          {isHeader ? (
            <>
              <DropdownMenuItem
                onClick={() => {
                  setOpen(false)
                  setDialogConfig({ open: true, kb: kbDetails || null })
                }}
                data-testid="edit-action"
              >
                Edit
              </DropdownMenuItem>
              <DropdownMenuItem
                onClick={() => {
                  setOpen(false)
                  toggleEnabledMutation.mutate()
                }}
                data-testid="toggle-enabled-action"
              >
                {kbDetails?.enabled ? 'Disable' : 'Enable'}
              </DropdownMenuItem>
              {(kbDetails?.sources[0]?.source_type === KBDocumentSource.URL ||
                kbDetails?.sources[0]?.source_type === KBDocumentSource.APP) &&
                kbDetails?.enabled && (
                  <DropdownMenuItem
                    onClick={() => {
                      setOpen(false)
                      resyncMutation.mutate()
                    }}
                    data-testid="resync-action"
                  >
                    Resync
                  </DropdownMenuItem>
                )}
              <DropdownMenuItem
                onClick={() => {
                  setOpen(false)
                  deleteMutation.mutate()
                }}
                data-testid="delete-action"
              >
                Delete
              </DropdownMenuItem>
            </>
          ) : (
            <>
              {source === KBDocumentSource.FILE && resolveDownloadUrl(row) && (
                <DropdownMenuItem
                  onClick={() => {
                    setOpen(false)
                    handleDownload()
                  }}
                  data-testid="download-action"
                >
                  Download
                </DropdownMenuItem>
              )}
              {!isSource && (
                <DropdownMenuItem
                  onClick={() => {
                    setOpen(false)
                    toggleEnabledMutation.mutate()
                  }}
                  data-testid="toggle-enabled-action"
                >
                  {enabled ? 'Disable' : 'Enable'}
                </DropdownMenuItem>
              )}
              {(source === KBDocumentSource.URL ||
                source === KBDocumentSource.APP ||
                (source === KBDocumentSource.FILE && isSource)) &&
                enabled && (
                  <DropdownMenuItem
                    onClick={() => {
                      setOpen(false)
                      resyncMutation.mutate()
                    }}
                    data-testid="resync-action"
                  >
                    Resync
                  </DropdownMenuItem>
                )}
              <DropdownMenuItem
                onClick={() => {
                  setOpen(false)
                  deleteMutation.mutate()
                }}
                data-testid="delete-action"
              >
                Delete
              </DropdownMenuItem>
            </>
          )}
        </DropdownMenuContent>
      </DropdownMenu>
      {isHeader && (
        <RenameKBDialog
          flows={flows}
          open={dialogConfig.open}
          setOpen={(isOpen) => setDialogConfig((prev) => ({ ...prev, open: isOpen }))}
          name={dialogConfig.kb?.name ?? ''}
          id={dialogConfig.kb?.id ?? ''}
          phlo_uuids={dialogConfig.kb?.phlo_uuids ?? []}
          linksUnavailable={dialogConfig.kb?.linksUnavailable ?? false}
          include_youtube_content={dialogConfig.kb?.include_youtube_content ?? false}
          include_image_content={dialogConfig.kb?.include_image_content ?? false}
          sync_frequency={{
            sync_period: dialogConfig.kb?.sync_period ?? 'MONTH',
            sync_frequency: dialogConfig.kb?.sync_frequency ?? 1,
          }}
          sync_enabled={dialogConfig.kb?.sync_enabled ?? false}
        />
      )}
    </>
  )
}
